/* @flow */
import * as React from 'react';
import { action, observable } from 'mobx';
import { translate } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import type { TFunction } from 'react-i18next';
import VirtualizedSelect from 'react-virtualized-select';
import createFilterOptions from 'react-select-fast-filter-options';

import Button from '@material-ui/core/Button';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';

import ActionsList from 'components/ActionsList/ActionsList';
import FormButton from 'forms/components/controls/FormButton';

import ElectroTextField from '../ElectroTextField/ElectroTextField';

import styles from './ElectroSelectExtra.module.scss';
import electroSelectStyles from '../ElectroSelect/ElectroSelect.module.scss';

type InnerProps = {
  model: Object,
  itemData: Object,
  modelChild: Object,
};

type TypeProps = {|
  t: TFunction,
  form: Object,
  field: Object,
  multi: Object,
  isFlex: boolean,
  formName: string,
  props: InnerProps,
  disabled: boolean,
  searchable?: boolean,
  onSetEdit?: () => void,
  extraFilters?: string[],
  labelSeparator?: string,
  resetModels?: () => void,
  onResetFunc?: () => void,
  exclude?: string | string[],
  labelKey?: string | string[],
  singleSelectedValue?: string,
  updateCustomFields?: () => void,
  /** If added, it will show a clearing
   * option with the string passed as label */
  clearOptionLabel?: string | null,
  handleOnChange?: (selectedValue: any) => void,
|};

@translate()
@inject('manufacturersProductsStore')
@observer
export default class ElectroSelectExtra extends React.Component<*> {
  @observable vModelData = this.props.props.model;

  @observable vModelDataChild = this.props.props.modelChild;

  static defaultProps = {
    isFlex: false,
  };

  @observable isFocused: boolean = false;

  componentDidMount = () => {
    const { field } = this.props;

    // This will prevent stale values in the virtualizedSelect
    if (!field.value && field.extra?.selectID) {
      field.set('extra', { ...field.extra, selectID: null });
    }

    if (field.extra && field.extra.parentId) {
      this.vModelData.filterId = field.extra.parentId;
    }

    this.vModelData.loadData();
  };

  /**
   * Gets field based on on these parameters
   * We need the field to retrieve the index
   * This allows us to be more flexible and have
   * more complex and nested forms
   * @param formName
   * @param form
   * @param fieldName
   * @param field | field.path = formName.field name OR formName.0.fieldName
   */
  getField(form: Object, formName: string, fieldName: string, field: Object = {}) {
    if (field.path) {
      // checking if the field is eg. formName.0.fieldName
      const parts = field.path.split('.');
      const fieldIndex = Number(parts[1]);
      const isNestedField = Number.isInteger(fieldIndex);

      if (isNestedField) {
        return form
          .$(`${formName}`)
          .$(fieldIndex)
          .$(`${fieldName}`);
      }

      return form.$(`${formName}.${fieldName}`);
    }

    return form.$(`${formName}.${fieldName}`);
  }

  @action toggleFocus = () => {
    this.isFocused = !this.isFocused;
  };

  /**
   * Sets selected values on fields form their setup extra object
   * @param selectedValue
   */
  setSelected = (selectedValue: Object) => {
    const { form, formName, field }: TypeProps = this.props;
    const fieldExtra: Object = field.extra;

    this.vModelData.search = '';

    if (fieldExtra.setSelectedFields && fieldExtra.setSelectedFields.length > 0) {
      for (let i: * = 0; i < fieldExtra.setSelectedFields.length; i += 1) {
        const retrievedField = this.getField(
          form,
          formName,
          fieldExtra.setSelectedFields[i].field,
          field
        );
        if (fieldExtra.setSelectedFields[i].condition) {
          retrievedField.value =
            selectedValue[`${fieldExtra.setSelectedFields[i].value}`] ===
            fieldExtra.setSelectedFields[i].condition.if
              ? fieldExtra.setSelectedFields[i].condition.ifVal
              : fieldExtra.setSelectedFields[i].condition.elseVal;
        } else if (selectedValue[`${fieldExtra.setSelectedFields[i].value}`]) {
          retrievedField.value = selectedValue[`${fieldExtra.setSelectedFields[i].value}`];
        } else {
          retrievedField.value = '';
          if (retrievedField.extra) retrievedField.extra.selectID = null;
        }
      }
    }
  };

  /**
   * Resets field values
   */
  setReset = () => {
    const { form, formName, field, onResetFunc, props }: TypeProps = this.props;
    const { itemData } = props;
    const fieldExtra: Object = field.extra;

    if (itemData.owned_manufacturer_id) {
      form.$(`${formName}.manufacturer`).extra.selectID = itemData.owned_manufacturer_id;
    }

    this.vModelData.search = '';

    if (fieldExtra.resetValueFields && fieldExtra.resetValueFields.length > 0) {
      for (let i: * = 0; i < fieldExtra.resetValueFields.length; i += 1) {
        const retrievedField = this.getField(
          form,
          formName,
          fieldExtra.resetValueFields[i].field,
          field
        );
        if (fieldExtra.resetValueFields[i].condition) {
          retrievedField.value =
            itemData[`${fieldExtra.resetValueFields[i].value}`] ===
            fieldExtra.resetValueFields[i].condition.if
              ? fieldExtra.resetValueFields[i].condition.ifVal
              : fieldExtra.resetValueFields[i].condition.elseVal;
        } else {
          retrievedField.value = itemData[`${fieldExtra.resetValueFields[i].value}`];
        }
      }
    }

    // This is a function for resetting values
    if (onResetFunc) {
      onResetFunc();
    }
  };

  setResult = (selectedValue: Object) => {
    const {
      field,
      multi,
      labelKey,
      handleOnChange,
      updateCustomFields,
      resetModels,
    }: TypeProps = this.props;
    const fieldExtra: Object = field.extra;

    if (!selectedValue) {
      if (handleOnChange) handleOnChange('');
      if (multi) field.set('');
      return;
    }

    if (selectedValue.id === '__clear__') {
      field.set('value', '');
      field.set('extra', { ...(field.extra || {}), selectID: null });
      return;
    }

    if (multi) {
      if (handleOnChange) handleOnChange(selectedValue);
      field.set(selectedValue);
    } else {
      if (labelKey instanceof Array) {
        field.set(
          'value',
          labelKey
            .reduce((keyArr: Array<*>, key: string) => {
              keyArr.push(selectedValue[key]);
              return keyArr;
            }, [])
            .join(' ')
        );
      } else {
        field.set('value', selectedValue[labelKey]);
      }

      field.set('extra', { ...fieldExtra, selectID: selectedValue.id });
      this.vModelData.setResult(selectedValue);

      this.setSelected(selectedValue);

      if (handleOnChange) {
        handleOnChange(selectedValue);
      }

      if (this.vModelDataChild && fieldExtra) {
        // In case we have defined more than one child
        // The param could be either a single object OR an array of objects
        if (this.vModelDataChild instanceof Array) {
          this.reloadChildren(selectedValue);
        } else {
          this.vModelDataChild.filterId = selectedValue.id;
          this.vModelDataChild.parentId = selectedValue.id;
          this.vModelDataChild.search = '';

          this.checkExtraFilters(selectedValue);
          this.vModelDataChild.loadData();
        }
      }
    }

    // This is a function for updating labels
    // and helper text of asset custom fields, due to account change
    if (updateCustomFields) {
      updateCustomFields();
    }

    // This is a function for resetting values of locations, due to account change
    if (resetModels) {
      resetModels();
    }
  };

  /**
   * Reloads each model that has been passed in through props
   * @param selectedValue
   */
  reloadChildren = (selectedValue: Object) => {
    for (let i: * = 0; i < this.vModelDataChild.length; i += 1) {
      this.vModelDataChild[i].filterId = selectedValue.id;
      this.vModelDataChild[i].parentId = selectedValue.id;
      this.vModelDataChild[i].search = '';

      this.checkExtraFilters(selectedValue);
      this.vModelDataChild[i].loadData();
    }
  };

  /**
   * Builds filters for our request to send to our API
   * This functionality is based of the form set up fields, eg. setUpAssetRequestForm.js
   * Here we have fields that have the extra object assigned, and the method below will
   * grab any configuration within that to build our queries
   * @param selectedValue
   */
  checkExtraFilters = (selectedValue: Object) => {
    const { form, field }: TypeProps = this.props;
    const fieldExtra: Object = field.extra;

    if (!fieldExtra?.childExtraFilters) return;

    // In case we need more than one filters like in locations
    // Data retrieved from setup Form -> extra -> childExtraFilters[]
    const filterArr: Array<string> = [];

    for (let i: * = 0; i < fieldExtra.childExtraFilters.length; i += 1) {
      const extraArr = fieldExtra.childExtraFilters[i];

      if (extraArr.type === 'formField' || extraArr.type === 'formFieldParam') {
        let filterField = null;

        try {
          filterField = this.getField(
            form,
            extraArr.formName,
            extraArr.formField,
            extraArr.isNested ? field : {}
          );
        } catch (err) {
          filterField = null;
        }

        if (filterField) {
          let filterValue;
          try {
            filterValue = extraArr.isExtra
              ? filterField.extra?.[extraArr.value]
              : filterField[extraArr.value];
          } catch (err) {
            filterValue = undefined;
          }

          if (extraArr.type === 'formField') {
            filterArr.push(`/${filterValue != null ? filterValue : ''}`);
          } else {
            filterArr.push(`${extraArr.paramName}=${filterValue != null ? filterValue : ''}`);
          }
        }
      } else {
        const id = selectedValue && selectedValue.id;
        if (id) {
          filterArr.push(extraArr.value + id);
        }
      }
    }

    this.vModelDataChild.filterId = filterArr.join('');
  };

  applyChanges = () => {
    const { form, formName, field, onSetEdit }: TypeProps = this.props;
    const fieldExtra: Object = field.extra;

    if (fieldExtra.toggleToTextField) {
      if (fieldExtra.isTextField) {
        field.set('extra', { ...fieldExtra, isTextField: false });

        if (onSetEdit) {
          onSetEdit();
        }
      } else {
        field.set('extra', { ...fieldExtra, isTextField: true });
        this.setReset();
      }
    }

    if (fieldExtra.toggleToDisable) {
      field.set('disabled', !field.disabled);
    }

    if (fieldExtra.buttonClick) {
      const targetField = form.$(`${formName}.${fieldExtra.buttonClick}`);
      const sameTextFieldState = targetField.extra?.isTextField === fieldExtra.isTextField;

      if (sameTextFieldState) {
        const button = document.getElementById(fieldExtra.buttonClick);
        if (button) button.click();
      }
    }
  };

  getTheData = () => {
    const vModelData: Object = this.vModelData;
    const { labelKey, labelSeparator, exclude, clearOptionLabel, field, t } = this.props;

    // concat Users
    let userArr: Array<Object> = [];
    const resp: Object = vModelData.list;

    for (const [key] of Object.entries(resp || {})) {
      userArr = userArr.concat(resp[key]);
    }

    let options = (resp && resp.data) || (resp && resp.active) || (resp && userArr) || resp;

    if (!options) return options;

    // An array of IDs to exclude can be passed and they will be removed from the options.
    if (exclude) options = options.filter((obj: Object) => !exclude.includes(obj.id));

    // This will inject an option that will clear the field and the dropdown.
    if (clearOptionLabel) options = [{ id: '__clear__', __info__: clearOptionLabel }, ...options];

    // This supports prefilled options via the field.extra.prefillData property.
    // If present, this object is added to the list of dropdown options
    // so the UI can display a pre-selected value even if it’s not yet in the model’s data.
    // The object should include the fields expected by labelKey for display and
    // the value property used by the MobX form.
    if (field.extra?.prefillData) options = [field.extra.prefillData, ...options];

    // With this way we can have 2 fields in item list
    // For example { first_name: John, last_name: Doe }
    // will result in a label like __info__: John Doe
    return options.map(s => {
      if (s.__info__ !== undefined) return s;
      return {
        ...s,
        __info__:
          labelKey instanceof Array
            ? labelKey
                .reduce((keyArr, key) => {
                  keyArr.push(s[key]);
                  return keyArr;
                }, [])
                .join(labelSeparator || ' ')
            : t(s[labelKey]),
      };
    });
  };

  render() {
    const {
      field,
      multi,
      disabled,
      isFlex,
      singleSelectedValue,
      searchable,
      extraFilters,
      t,
    }: TypeProps = this.props;
    const {
      props: { itemData, addNewButton },
      actionButtonData,
    } = this.props;
    const fieldExtra = field.extra;
    const { vModelData } = this;

    const isFocusedClass = this.isFocused ? electroSelectStyles['is-focused'] : '';
    const isErrorClass = field.error ? electroSelectStyles['is-error'] : '';
    const isDisabledClass = disabled ? electroSelectStyles['is-disabled'] : '';
    const isFlexClass = isFlex ? styles['is-flex'] : '';
    const isRequired: boolean = Boolean(
      field.rules && field.rules.includes('required') && !field.rules.includes('userRoleCheck')
    );

    const options = this.getTheData();
    const filterOptions = createFilterOptions({
      indexes:
        extraFilters && extraFilters.length > 0 ? ['__info__', ...extraFilters] : ['__info__'],
      options,
      valueKey: 'id',
    });

    let selection = '';
    if (multi) {
      selection = singleSelectedValue;
    } else if (fieldExtra) {
      selection = fieldExtra.selectID;
    }

    return (
      <div className={`${styles['select-wrapper']} ${isFlexClass}`}>
        {itemData && fieldExtra && fieldExtra.isTextField ? (
          <ElectroTextField margin="none" field={field} />
        ) : (
          <div
            className={`${
              electroSelectStyles['electro-select']
            } ${isFocusedClass} ${isErrorClass} ${isDisabledClass}`}
          >
            {searchable && (
              <svg className={electroSelectStyles['search-icon']}>
                <use xlinkHref="#icon-search" />
              </svg>
            )}
            <FormControl disabled={disabled}>
              <InputLabel
                className={electroSelectStyles['custom-placeholder']}
                required={isRequired}
                shrink={Boolean(field.value || this.isFocused)}
              >
                {field.label}
              </InputLabel>
              <VirtualizedSelect
                {...field.bind({ vModelData })}
                // async={true}
                // loadOptions={(): void => vModelData.loadData()}
                arrowRenderer={() =>
                  !disabled && (
                    <svg className={electroSelectStyles['dropdown-icon']}>
                      <use xlinkHref="#icon-drop-arrow-down" />
                    </svg>
                  )
                }
                className={electroSelectStyles['electro-select-field']}
                options={options}
                filterOptions={filterOptions}
                clearable={multi || false}
                searchable
                required={isRequired}
                multi={multi || false}
                simpleValue={multi || false}
                onChange={(selectedValue: Object) => this.setResult(selectedValue)}
                isLoading={vModelData.loading}
                onFocus={this.toggleFocus}
                onBlur={this.toggleFocus}
                inputProps={{
                  autoComplete: 'off',
                }}
                noResultsText={t('general:search_results.no_results_found')}
                loadingPlaceholder=""
                placeholder={null}
                helperText={field.showError ? field.error : ''}
                value={selection}
                name={field.name}
                labelKey="__info__"
                valueKey="id"
                disabled={disabled}
              />
              {// field.value === '' &&
              field.error && <FormHelperText error>{field.error}</FormHelperText>}
            </FormControl>
          </div>
        )}
        {addNewButton && (
          <FormButton isFlex={isFlex}>
            <NavLink to={addNewButton.path}>
              <Button color="default" variant="outlined">
                <svg className="button-icon">
                  <use xlinkHref="#icon-add" />
                </svg>
                {addNewButton.title}
              </Button>
            </NavLink>
          </FormButton>
        )}
        {!addNewButton &&
          fieldExtra &&
          !(Object.keys(fieldExtra).length === 0 && fieldExtra.constructor === Object) && (
            <React.Fragment>
              {fieldExtra.resetValue && (
                <FormButton isFlex={isFlex}>
                  <Button
                    color="default"
                    id={field.name}
                    variant="outlined"
                    onClick={() => {
                      this.applyChanges();
                    }}
                  >
                    <svg className="button-icon">
                      <use xlinkHref="#icon-add" />
                    </svg>
                    {fieldExtra.isTextField ? fieldExtra.textTitle : fieldExtra.title}
                  </Button>
                </FormButton>
              )}
            </React.Fragment>
          )}
        {fieldExtra && fieldExtra.actionButton && actionButtonData && (
          <FormButton isFlex={isFlex}>
            <ActionsList
              actions={actionButtonData.data}
              disabled={actionButtonData.disabled}
              singleAction
            />
          </FormButton>
        )}
      </div>
    );
  }
}
